import Err from 'models/err';
import GatewayErrorInteractiveHandler from 'scripts/application/lib/gateway_error_interactive_handler';
import ShowToast from 'actions/toast_deprecated/show_toast';
import { ToastType } from 'models/toast_deprecated';

// UpdateTaskCustomAttributes adds and/or removes custom-attribute values on a
// task.
//
// Mirrors UpdateConversationCustomAttributes but targets the task-scoped REST
// endpoint added in supernova:
//   POST /api/v1/orgs/:orgId/tasks/:taskId/custom-attributes
// The body is the shared CustomAttributeUpdate shape:
//   { add: [{ key, value }], remove: [{ key, value }] }
// The UI works in terms of attribute id + value, so we translate id -> key
// against the shared org custom-attribute registry before posting (the
// registry is shared with conversations). The initiator is derived
// server-side from the JWT.
//
// The store is updated optimistically, like every sibling task mutation
// (UpdateTaskAssignee, MarkTaskClosed). The server's task-update broadcast
// still reconciles the item, but waiting for it would leave the multi-select
// diffing against stale customAttributes — so removing a pill and then adding
// an unrelated value would re-add the one just removed.
export default class UpdateTaskCustomAttributes {
  constructor(context) {
    this.context = context;
  }

  run({ taskId, customAttributeUpdates }) {
    const add = customAttributeUpdates.add || [];
    const remove = customAttributeUpdates.remove || [];
    if (!add.length && !remove.length) {
      return;
    }

    const keysById = this._keysById([...add, ...remove]);
    const hasKey = attr => keysById[attr.id] !== undefined;
    const toKeyValue = attr => ({ key: keysById[attr.id], value: attr.value });

    // Drop ids we can't resolve to a key (stale UI state, a concurrently removed
    // definition) rather than POST a payload the server can't interpret.
    const resolvedAdd = add.filter(hasKey);
    const resolvedRemove = remove.filter(hasKey);
    if (!resolvedAdd.length && !resolvedRemove.length) {
      return;
    }

    const body = {};
    if (resolvedAdd.length) {
      body.add = resolvedAdd.map(toKeyValue);
    }
    if (resolvedRemove.length) {
      body.remove = resolvedRemove.map(toKeyValue);
    }

    this._applyOptimisticUpdate(taskId, resolvedAdd, resolvedRemove);

    this.context.gateways.taskCustomAttributes.add({ taskId }, body).catch(err => this._handleError(err));

    // Track keys and counts only — attribute values are org-defined and may hold PII.
    this.context.analytics.track('Task Custom Attributes Updated', {
      taskId,
      customAttributesAdded: resolvedAdd.map(attr => keysById[attr.id]),
      customAttributesRemoved: resolvedRemove.map(attr => keysById[attr.id]),
      addedCount: resolvedAdd.length,
      removedCount: resolvedRemove.length,
    });
  }

  _applyOptimisticUpdate(taskId, add, remove) {
    const store = this.context.stores.conversationHistory;
    // findBy rather than find: the pills can outlive the item in the store
    // (e.g. the feed switched customers mid-request) and find() throws.
    const taskItem = store.findBy({ id: taskId });
    if (!taskItem) {
      return;
    }

    taskItem.content.applyCustomAttributeChanges({ add, remove });
    store.setPending(taskItem);
  }

  _keysById(attributes) {
    const ids = new Set(attributes.map(attr => attr.id));
    const existing = this.context.stores.customAttributes.findAll({
      filter: attr => ids.has(attr.id),
      select: ['id', 'key'],
    });
    return existing.reduce((acc, attr) => {
      acc[attr.id] = attr.key;
      return acc;
    }, {});
  }

  _handleError(err) {
    if (err.errors && err.errors.length) {
      const error = err.errors[0];
      if (error.code === Err.Code.TOO_LONG) {
        this._showToast(
          ToastType.ERROR,
          'The value you entered exceeds the 255 character limit. Please remove it and try again.'
        );
        return;
      }
      if (error.code === Err.Code.INVALID && error.detail === 'key is archived') {
        this._showToast(
          ToastType.ERROR,
          'The attribute you selected is now archived. Please remove it and try again or activate the attribute.'
        );
        return;
      }
    }
    new GatewayErrorInteractiveHandler(this.context).handleCommonErrors(err);
  }

  _showToast(type, message) {
    this.context.executeAction(ShowToast, { type, message });
  }
}
